import { Gltf, GltfAnimationChannel, GltfPrimitive, GltfType } from './GltfTypes';
import { getNumComponents } from './GltfFunctions';
import { mat4, mat3, vec3 } from 'gl-matrix';
import { readMeshTransform, readModelTransform } from './ModelTransformExtras';

export type ResolvedAttribute = {
  array: TypedArray;
  type: GltfType;
};

export type ResolvedPrimitive = {
  attributes: { [semantic: string]: ResolvedAttribute };
  indices?: Uint16Array | Uint32Array;
  materialIndex?: number;
  lightmapPage?: number;
  extras?: Record<string, any>;
};

export interface ResolvedMesh {
  name: string;
  primitives: ResolvedPrimitive[];
}

export type ResolvedAnimation = {
  name?: string;
  channels: GltfAnimationChannel[];
  samplers: {
    input: TypedArray;
    output: TypedArray;
    interpolation: string;
  }[];
};

export interface ResolvedScene {
  meshes: ResolvedMesh[];
}

export type ResolvedTextureData = {
  imageData: Uint8Array;
  mimeType: string;
};

export type ResolvedMaterialTextures = {
  materialIndex: number;
  baseColor?: ResolvedTextureData;
  normalMap?: ResolvedTextureData;
  metallicRoughness?: ResolvedTextureData;
  alphaMode?: 'OPAQUE' | 'MASK' | 'BLEND';
};

type TypedArray = Float32Array | Uint32Array | Uint16Array | Uint8Array | Int16Array | Int8Array;

export class GltfParser {
  private readonly data: DataView;

  constructor(private readonly buffer: Uint8Array) {
    this.data = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  }

  parse(): { json: Gltf; binaries: Uint8Array[] } {
    // GLB Header
    const magic = this.data.getUint32(0, true);
    const version = this.data.getUint32(4, true);
    const length = this.data.getUint32(8, true);

    if (magic !== 0x46546c67) {
      // 'glTF'
      throw new Error('Invalid glTF binary file (bad magic).');
    }
    if (version !== 2) {
      throw new Error(`Unsupported glTF version: ${version}`);
    }
    if (length !== this.buffer.byteLength) {
      throw new Error('GLB length does not match buffer length.');
    }

    let offset = 12;
    let json: Gltf | null = null;
    const binaries: Uint8Array[] = [];

    while (offset < length) {
      const chunkLength = this.data.getUint32(offset, true);
      const chunkType = this.data.getUint32(offset + 4, true);
      const chunkData = this.buffer.subarray(offset + 8, offset + 8 + chunkLength);

      if (chunkType === 0x4e4f534a) {
        // JSON
        const text = new TextDecoder().decode(chunkData);
        json = JSON.parse(text);
      } else if (chunkType === 0x004e4942) {
        // BIN — copy into a freshly-allocated buffer so byteOffset is 0 and
        // Float32Array views are guaranteed 4-byte aligned. GLBs whose JSON
        // chunk isn't 4-padded would otherwise leave BIN at a non-aligned
        // offset within the source ArrayBuffer.
        const aligned = new Uint8Array(chunkLength);
        aligned.set(chunkData);
        binaries.push(aligned);
      }

      offset += 8 + chunkLength;
    }

    if (!json) {
      throw new Error('GLB did not contain JSON chunk.');
    }

    return { json, binaries };
  }

  getTypedArray(
    componentType: number,
    buffer: ArrayBufferLike,
    byteOffset: number,
    length: number,
  ): TypedArray {
    // Typed-array views require byteOffset to be a multiple of the element
    // size. GLBs that don't pad preceding image/binary chunks can leave an
    // accessor's offset misaligned. Copy into a fresh buffer when needed.
    const align = (bytesPerElement: number): ArrayBufferLike => {
      if (byteOffset % bytesPerElement === 0) return buffer;
      const copy = new Uint8Array(length * bytesPerElement);
      copy.set(new Uint8Array(buffer, byteOffset, length * bytesPerElement));
      byteOffset = 0;
      return copy.buffer;
    };
    switch (componentType) {
      case 5120:
        return new Int8Array(buffer, byteOffset, length); // BYTE
      case 5121:
        return new Uint8Array(buffer, byteOffset, length); // UNSIGNED_BYTE
      case 5122:
        return new Int16Array(align(2), byteOffset, length); // SHORT
      case 5123:
        return new Uint16Array(align(2), byteOffset, length); // UNSIGNED_SHORT
      case 5125:
        return new Uint32Array(align(4), byteOffset, length); // UNSIGNED_INT
      case 5126:
        return new Float32Array(align(4), byteOffset, length); // FLOAT
      default:
        throw new Error(`Unknown componentType: ${componentType}`);
    }
  }

  private resolvePrimitive(
    json: Gltf,
    binaries: Uint8Array[],
    primitive: GltfPrimitive,
  ): ResolvedPrimitive {
    const attributes: Record<string, ResolvedAttribute> = {};

    for (const [name, accessorIndex] of Object.entries(primitive.attributes)) {
      const accessor = json.accessors![accessorIndex];
      const bufferView = json.bufferViews![accessor.bufferView!];
      const bin = binaries[bufferView.buffer];

      const numComponents = getNumComponents(accessor.type);
      const length = accessor.count * numComponents;
      const byteOffset = (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);

      attributes[name] = {
        type: accessor.type,
        array: this.getTypedArray(
          accessor.componentType,
          bin.buffer,
          bin.byteOffset + byteOffset,
          length,
        ),
      };
    }

    let indices: Uint16Array | Uint32Array | undefined = undefined;
    if (primitive.indices !== undefined) {
      const accessor = json.accessors![primitive.indices];
      const bufferView = json.bufferViews![accessor.bufferView!];
      const bin = binaries[bufferView.buffer];

      const length = accessor.count;
      const byteOffset = (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);

      indices = this.getTypedArray(
        accessor.componentType,
        bin.buffer,
        bin.byteOffset + byteOffset,
        length,
      ) as Uint16Array | Uint32Array;
    }

    return {
      attributes,
      indices,
      materialIndex: primitive.material,
      lightmapPage: primitive.extras?.['lightmapPage'] as number | undefined,
      extras: primitive.extras,
    };
  }

  /**
   * Extract a GLB-embedded image's raw bytes + mime type by its index in
   * `json.images`. Returns undefined if the image has no bufferView (e.g.
   * external URI).
   */
  static extractImageByIndex(
    json: Gltf,
    binaries: Uint8Array[],
    imageIndex: number,
  ): ResolvedTextureData | undefined {
    if (!json.images || imageIndex < 0 || imageIndex >= json.images.length) return undefined;
    const image = json.images[imageIndex];
    if (image.bufferView === undefined) return undefined;
    const bufferView = json.bufferViews![image.bufferView];
    const bin = binaries[bufferView.buffer];
    const byteOffset = bufferView.byteOffset ?? 0;
    const imageData = bin.slice(byteOffset, byteOffset + bufferView.byteLength);
    return { imageData, mimeType: image.mimeType ?? 'image/png' };
  }

  resolveTextures(json: Gltf, binaries: Uint8Array[]): ResolvedMaterialTextures[] {
    if (!json.materials) return [];

    const results: ResolvedMaterialTextures[] = [];

    for (let i = 0; i < json.materials.length; i++) {
      const material = json.materials[i];
      const baseColor = this.extractTextureImage(
        json,
        binaries,
        material.pbrMetallicRoughness?.baseColorTexture,
      );
      const normalMap = this.extractTextureImage(json, binaries, material.normalTexture);
      const metallicRoughness = this.extractTextureImage(
        json,
        binaries,
        material.pbrMetallicRoughness?.metallicRoughnessTexture,
      );
      if (!baseColor && !normalMap && !metallicRoughness) continue;

      results.push({
        materialIndex: i,
        baseColor,
        normalMap,
        metallicRoughness,
        alphaMode: material.alphaMode,
      });
    }

    return results;
  }

  private extractTextureImage(
    json: Gltf,
    binaries: Uint8Array[],
    texInfo?: { index: number },
  ): ResolvedTextureData | undefined {
    if (!texInfo || !json.textures || !json.images) return undefined;

    const gltfTexture = json.textures[texInfo.index];
    if (gltfTexture.source === undefined) return undefined;

    const image = json.images[gltfTexture.source];
    if (image.bufferView === undefined) return undefined;

    const bufferView = json.bufferViews![image.bufferView];
    const bin = binaries[bufferView.buffer];
    const byteOffset = bufferView.byteOffset ?? 0;
    const imageData = bin.slice(byteOffset, byteOffset + bufferView.byteLength);

    return { imageData, mimeType: image.mimeType ?? 'image/png' };
  }

  resolveAllMeshes(json: Gltf, binaries: Uint8Array[]): ResolvedMesh[] {
    if (!json.meshes) return [];

    return json.meshes.map((mesh, i) => ({
      name: mesh.name ?? `mesh_${i}`,
      primitives: mesh.primitives.map((p) => this.resolvePrimitive(json, binaries, p)),
    }));
  }

  resolveSceneMeshes(json: Gltf, binaries: Uint8Array[]): ResolvedMesh[] {
    if (!json.nodes || !json.meshes) return [];

    const scene = json.scenes?.[json.scene ?? 0];
    if (!scene?.nodes) return [];

    // Bake _MODEL_TRANSFORM (root extras) into every scene mesh so static
    // vertex positions match what the backend's GltfGeometryExtractor produces
    // for navmesh/volgrid extraction. Animated meshes consume rawMeshes (no
    // bake) and apply the same transform via obj.trs at runtime.
    //
    // Node-level TRS (translation/rotation/scale on glTF nodes) is intentionally
    // ignored for static meshes: the engine's convention is that all spatial
    // info for a static model lives in vertex positions + _MODEL_TRANSFORM +
    // _MESH_TRANSFORM. Composing node transforms on top would double-apply
    // rotation for models like Sponza whose source GLB encodes a Y-up
    // correction at the node level (and again via _MODEL_TRANSFORM).
    const rootBase = mat4.create();
    composeAssetTransform(rootBase, readModelTransform(json));

    const result: ResolvedMesh[] = [];

    const traverse = (nodeIndex: number) => {
      const node = json.nodes![nodeIndex];

      if (node.mesh !== undefined) {
        const gltfMesh = json.meshes![node.mesh];
        const meshName = gltfMesh.name ?? node.name ?? `mesh_${node.mesh}`;

        // Compose per-mesh _MESH_TRANSFORM onto the root _MODEL_TRANSFORM.
        const meshWorld = mat4.clone(rootBase);
        composeAssetTransform(meshWorld, readMeshTransform(json, meshName));

        const normalMat = mat3.create();
        mat3.fromMat4(normalMat, meshWorld);
        mat3.invert(normalMat, normalMat);
        mat3.transpose(normalMat, normalMat);

        const primitives = gltfMesh.primitives.map((p) => {
          const resolved = this.resolvePrimitive(json, binaries, p);
          this.transformPrimitive(resolved, meshWorld, normalMat);
          return resolved;
        });

        result.push({ name: meshName, primitives });
      }

      if (node.children) {
        for (const child of node.children) {
          traverse(child);
        }
      }
    };

    for (const rootIndex of scene.nodes) {
      traverse(rootIndex);
    }

    return result;
  }

  private transformPrimitive(prim: ResolvedPrimitive, world: mat4, normalMat: mat3): void {
    const pos = prim.attributes['POSITION'];
    if (pos) {
      const src = pos.array as Float32Array;
      const dst = new Float32Array(src.length);
      const v = vec3.create();
      for (let i = 0; i < src.length; i += 3) {
        vec3.set(v, src[i], src[i + 1], src[i + 2]);
        vec3.transformMat4(v, v, world);
        dst[i] = v[0];
        dst[i + 1] = v[1];
        dst[i + 2] = v[2];
      }
      prim.attributes['POSITION'] = { array: dst, type: pos.type };
    }

    const norm = prim.attributes['NORMAL'];
    if (norm) {
      const src = norm.array as Float32Array;
      const dst = new Float32Array(src.length);
      const n = vec3.create();
      for (let i = 0; i < src.length; i += 3) {
        vec3.set(n, src[i], src[i + 1], src[i + 2]);
        vec3.transformMat3(n, n, normalMat);
        vec3.normalize(n, n);
        dst[i] = n[0];
        dst[i + 1] = n[1];
        dst[i + 2] = n[2];
      }
      prim.attributes['NORMAL'] = { array: dst, type: norm.type };
    }
  }

  resolveAllAnimations(json: Gltf, binaries: Uint8Array[]): ResolvedAnimation[] {
    if (!json.animations) return [];

    return json.animations.map((animation) => {
      const samplers = animation.samplers.map((sampler) => {
        const inputAccessor = json.accessors![sampler.input];
        const outputAccessor = json.accessors![sampler.output];
        const inputBufferView = json.bufferViews![inputAccessor.bufferView!];
        const outputBufferView = json.bufferViews![outputAccessor.bufferView!];
        const inputBin = binaries[inputBufferView.buffer];
        const outputBin = binaries[outputBufferView.buffer];
        const inputArray = this.getTypedArray(
          inputAccessor.componentType,
          inputBin.buffer,
          inputBin.byteOffset + (inputBufferView.byteOffset ?? 0) + (inputAccessor.byteOffset ?? 0),
          inputAccessor.count,
        );
        const outputArray = this.getTypedArray(
          outputAccessor.componentType,
          outputBin.buffer,
          outputBin.byteOffset +
            (outputBufferView.byteOffset ?? 0) +
            (outputAccessor.byteOffset ?? 0),
          outputAccessor.count * getNumComponents(outputAccessor.type),
        );
        return {
          input: inputArray,
          output: outputArray,
          interpolation: sampler.interpolation || 'LINEAR',
        };
      });
      return {
        name: animation.name,
        channels: animation.channels,
        samplers,
      };
    });
  }
}

/**
 * Post-multiply `out` by an asset-level TRS (quaternion rotation, uniform scale)
 * matching the convention used by `_MODEL_TRANSFORM` and `_MESH_TRANSFORM`
 * extras and the backend `GltfGeometryExtractor.readModelTransform`.
 */
function composeAssetTransform(
  out: mat4,
  transform: {
    position?: [number, number, number];
    rotation?: [number, number, number, number];
    scale?: number;
  } | null,
): void {
  if (!transform) return;
  if (transform.position) {
    mat4.translate(out, out, transform.position);
  }
  if (transform.rotation) {
    const rotMat = mat4.fromQuat(mat4.create(), transform.rotation);
    mat4.multiply(out, out, rotMat);
  }
  if (transform.scale !== undefined && transform.scale !== 1) {
    mat4.scale(out, out, [transform.scale, transform.scale, transform.scale]);
  }
}
